import {
  OrganizationLogoMutationV1Schema,
  OrganizationSettingsV1Schema,
  PRODUCT_SESSION_POLICY,
  type OrganizationSettingsV1,
  type UpdateOrganizationSettingsV1,
} from "@fulcrum5/suite-contracts";
import { ImagePlus, LoaderCircle, RotateCw, Trash2, Upload } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
} from "react";
import { PlatformApiError } from "../api/platformApi.ts";
import { usePlatformSession } from "../session/PlatformSessionProvider.tsx";
import { useOrganizationResource } from "./useOrganizationResource.ts";
import "./dashboardPages.css";

const SETTINGS_PATH = (organizationId: string) => `/api/organizations/${organizationId}/settings`;
const MAX_LOGO_BYTES = 1024 * 1024;
const ACCEPTED_LOGO_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const LOGO_ACCEPT = "image/png,image/jpeg,image/webp";
const TIMEZONES = ["America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles", "Europe/London", "UTC"] as const;
const LOCALES = ["en-US", "en-CA", "en-GB"] as const;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

type FieldErrors = Readonly<Record<string, string>>;
type Notice = { readonly kind: "success" | "error"; readonly message: string } | null;

function draftFrom(settings: OrganizationSettingsV1): UpdateOrganizationSettingsV1 {
  return {
    displayName: settings.displayName,
    timezone: settings.timezone,
    locale: settings.locale,
    productSessionIdleMinutes: settings.productSessionIdleMinutes,
    productSessionMaximumHours: settings.productSessionMaximumHours,
    analyticsVisibility: settings.analyticsVisibility,
    costVisibility: settings.costVisibility,
  };
}

function selectOptions(values: readonly string[], current: string): readonly string[] {
  return values.includes(current) ? values : [current, ...values];
}

function fieldError(errors: FieldErrors, field: string): string | undefined {
  return errors[field] ?? errors[`/${field}`];
}

function mutationMessage(error: unknown, fallback: string): string {
  return error instanceof PlatformApiError ? error.message : fallback;
}

function settingsFromLogoMutation(settings: OrganizationSettingsV1, logoUrl: string | null, versionTag: string): OrganizationSettingsV1 {
  return { ...settings, logoUrl, versionTag };
}

function SettingsField({
  children,
  error,
  id,
  label,
}: {
  readonly children: JSX.Element;
  readonly error?: string;
  readonly id: string;
  readonly label: string;
}): JSX.Element {
  return (
    <label className="platform-field settings-page__field" htmlFor={id}>
      <span>{label}</span>
      {children}
      {error ? <span className="platform-field__error" id={`${id}-error`}>{error}</span> : null}
    </label>
  );
}

function VisibilityControl({
  disabled,
  label,
  onChange,
  value,
}: {
  readonly disabled: boolean;
  readonly label: string;
  readonly onChange: (value: "owners" | "owners_admins") => void;
  readonly value: "owners" | "owners_admins";
}): JSX.Element {
  return (
    <div aria-label={label} className="settings-page__visibility" role="radiogroup">
      <span>{label}</span>
      <div className="settings-page__segments">
        <button aria-checked={value === "owners"} disabled={disabled} onClick={() => onChange("owners")} role="radio" type="button">Owners only</button>
        <button aria-checked={value === "owners_admins"} disabled={disabled} onClick={() => onChange("owners_admins")} role="radio" type="button">Owners and admins</button>
      </div>
    </div>
  );
}

export function SettingsPage(): JSX.Element {
  const session = usePlatformSession();
  const resource = useOrganizationResource({
    path: SETTINGS_PATH,
    responseSchema: OrganizationSettingsV1Schema,
  });
  const [draft, setDraft] = useState<UpdateOrganizationSettingsV1 | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [notice, setNotice] = useState<Notice>(null);
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoDragging, setLogoDragging] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoDragDepth = useRef(0);
  const logoDropzoneId = useId();

  const settings = resource.data;
  const canEdit = settings?.canEdit === true;

  useEffect(() => {
    if (!settings) return;
    setDraft(draftFrom(settings));
    setFieldErrors({});
    setStale(false);
  }, [settings?.organizationId, settings?.versionTag]);

  useEffect(() => {
    setLogoFile(null);
    setLocalPreviewUrl(null);
    setLogoError(null);
  }, [settings?.organizationId]);

  useEffect(() => {
    if (!localPreviewUrl) return;
    return () => URL.revokeObjectURL(localPreviewUrl);
  }, [localPreviewUrl]);

  const timezoneOptions = useMemo(() => selectOptions(TIMEZONES, draft?.timezone ?? settings?.timezone ?? "UTC"), [draft?.timezone, settings?.timezone]);
  const localeOptions = useMemo(() => selectOptions(LOCALES, draft?.locale ?? settings?.locale ?? "en-US"), [draft?.locale, settings?.locale]);

  const updateDraft = useCallback(<Key extends keyof UpdateOrganizationSettingsV1>(key: Key, value: UpdateOrganizationSettingsV1[Key]) => {
    setDraft((current) => current ? { ...current, [key]: value } : current);
    setFieldErrors((current) => {
      if (!(key in current)) return current;
      const { [key]: _removed, ...remaining } = current;
      return remaining;
    });
    setNotice(null);
  }, []);

  const reload = useCallback(() => {
    setNotice(null);
    setStale(false);
    void resource.reload();
  }, [resource]);

  const save = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!settings || !draft || !canEdit || saving) return;
    setSaving(true);
    setFieldErrors({});
    setNotice(null);
    setStale(false);
    try {
      const result = await session.api.patch({
        path: SETTINGS_PATH(settings.organizationId),
        responseSchema: OrganizationSettingsV1Schema,
        body: draft,
        ifMatch: settings.versionTag,
      });
      resource.replaceData(result.data);
      setNotice({ kind: "success", message: "Settings saved" });
    } catch (error) {
      if (error instanceof PlatformApiError && error.status === 422) {
        setFieldErrors(error.error.fieldErrors ?? {});
        setNotice({ kind: "error", message: "Review the highlighted settings." });
      } else if (error instanceof PlatformApiError && error.status === 409) {
        setStale(true);
      } else {
        setNotice({ kind: "error", message: mutationMessage(error, "Settings could not be saved.") });
      }
    } finally {
      setSaving(false);
    }
  }, [canEdit, draft, resource, saving, session.api, settings]);

  const selectLogoFile = useCallback((nextFile: File | null) => {
    setLogoError(null);
    setNotice(null);
    if (!nextFile) {
      setLogoFile(null);
      setLocalPreviewUrl(null);
      return;
    }
    if (!ACCEPTED_LOGO_TYPES.has(nextFile.type)) {
      setLogoFile(null);
      setLocalPreviewUrl(null);
      setLogoError("Choose a PNG, JPEG, or WebP image.");
      return;
    }
    if (nextFile.size > MAX_LOGO_BYTES) {
      setLogoFile(null);
      setLocalPreviewUrl(null);
      setLogoError("Logo images must be 1 MiB or smaller.");
      return;
    }
    setLogoFile(nextFile);
    setLocalPreviewUrl(URL.createObjectURL(nextFile));
  }, []);

  const onLogoDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canEdit || logoBusy) return;
    logoDragDepth.current += 1;
    setLogoDragging(true);
  }, [canEdit, logoBusy]);

  const onLogoDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    logoDragDepth.current = Math.max(0, logoDragDepth.current - 1);
    if (logoDragDepth.current === 0) setLogoDragging(false);
  }, []);

  const onLogoDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canEdit || logoBusy) return;
    event.dataTransfer.dropEffect = "copy";
  }, [canEdit, logoBusy]);

  const onLogoDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    logoDragDepth.current = 0;
    setLogoDragging(false);
    if (!canEdit || logoBusy) return;
    const file = event.dataTransfer.files?.[0] ?? null;
    selectLogoFile(file);
  }, [canEdit, logoBusy, selectLogoFile]);

  const uploadLogo = useCallback(async () => {
    if (!settings || !logoFile || !canEdit || logoBusy) return;
    setLogoBusy(true);
    setLogoError(null);
    setNotice(null);
    setStale(false);
    try {
      const result = await session.api.putBytes({
        path: `/api/organizations/${settings.organizationId}/logo`,
        responseSchema: OrganizationLogoMutationV1Schema,
        bytes: logoFile,
        contentType: logoFile.type,
        ifMatch: settings.versionTag,
      });
      resource.replaceData(settingsFromLogoMutation(settings, result.data.logoUrl, result.data.versionTag));
      setLogoFile(null);
      setLocalPreviewUrl(null);
      setNotice({ kind: "success", message: "Logo updated" });
    } catch (error) {
      if (error instanceof PlatformApiError && error.status === 409) setStale(true);
      else setLogoError(mutationMessage(error, "Logo could not be updated."));
    } finally {
      setLogoBusy(false);
    }
  }, [canEdit, logoBusy, logoFile, resource, session.api, settings]);

  const removeLogo = useCallback(async () => {
    if (!settings || !settings.logoUrl || !canEdit || logoBusy) return;
    setLogoBusy(true);
    setLogoError(null);
    setNotice(null);
    setStale(false);
    try {
      const result = await session.api.delete({
        path: `/api/organizations/${settings.organizationId}/logo`,
        responseSchema: OrganizationLogoMutationV1Schema,
        ifMatch: settings.versionTag,
      });
      resource.replaceData(settingsFromLogoMutation(settings, result.data.logoUrl, result.data.versionTag));
      setNotice({ kind: "success", message: "Logo removed" });
    } catch (error) {
      if (error instanceof PlatformApiError && error.status === 409) setStale(true);
      else setLogoError(mutationMessage(error, "Logo could not be removed."));
    } finally {
      setLogoBusy(false);
    }
  }, [canEdit, logoBusy, resource, session.api, settings]);

  if (resource.status === "loading" && !settings) {
    return <section className="platform-page dashboard-page"><p className="platform-loading" role="status">Loading settings.</p></section>;
  }
  if (!settings && resource.status === "idle") {
    return <section className="platform-page dashboard-page"><p className="platform-empty-state" role="status">An active organization is required to view settings.</p></section>;
  }
  if (!settings || !draft) {
    return (
      <section className="platform-page dashboard-page">
        <div className="platform-notice platform-notice--error" role="alert">
          <p>{resource.message ?? "Settings could not be loaded."}</p>
          {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={reload} type="button">Retry settings</button> : null}
        </div>
      </section>
    );
  }

  const logoPreview = localPreviewUrl ?? settings.logoUrl;
  const nameError = fieldError(fieldErrors, "displayName");
  const timezoneError = fieldError(fieldErrors, "timezone");
  const localeError = fieldError(fieldErrors, "locale");
  const idleError = fieldError(fieldErrors, "productSessionIdleMinutes");
  const maximumError = fieldError(fieldErrors, "productSessionMaximumHours");

  return (
    <section className="platform-page dashboard-page settings-page" aria-labelledby="settings-page-title">
      <header className="dashboard-page__header">
        <div>
          <p className="platform-page__eyebrow">Workspace</p>
          <h1 id="settings-page-title">Settings</h1>
        </div>
        <span className="platform-badge" data-tone={canEdit ? "admin" : "inactive"}>{canEdit ? "Editable" : "Read only"}</span>
      </header>

      {!canEdit ? <p className="settings-page__read-only">Only organization owners and admins can edit these settings.</p> : null}
      {resource.status === "error" ? <div className="platform-notice platform-notice--error dashboard-page__notice" role="alert"><p>{resource.message ?? "Settings could not be refreshed."}</p>{resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={reload} type="button">Retry settings</button> : null}</div> : null}
      {notice ? <div className={`platform-notice ${notice.kind === "success" ? "platform-notice--success" : "platform-notice--error"} dashboard-page__notice`} role={notice.kind === "error" ? "alert" : "status"}><p>{notice.message}</p></div> : null}
      {stale ? <div className="platform-notice platform-notice--warning dashboard-page__notice" role="alert"><p><strong>Settings changed elsewhere</strong><br />Reload the current server snapshot before making further changes.</p><button className="platform-button platform-button--secondary" onClick={reload} type="button">Reload settings</button></div> : null}

      <section className="settings-page__logo-section" aria-labelledby="settings-logo-title">
        <div className="dashboard-section-heading">
          <div>
            <p className="dashboard-kicker">Brand</p>
            <h2 id="settings-logo-title">Organization logo</h2>
          </div>
        </div>
        <div className="settings-page__logo-row">
          <div className="settings-page__logo-preview" aria-label="Organization logo preview">
            {logoPreview ? <img alt="Organization logo preview" src={logoPreview} /> : <ImagePlus aria-hidden="true" size={24} strokeWidth={1.6} />}
          </div>
          <div className="settings-page__logo-controls">
            <input
              accept={LOGO_ACCEPT}
              aria-labelledby={logoDropzoneId}
              className="settings-page__logo-input"
              disabled={!canEdit || logoBusy}
              id="organization-logo"
              onChange={(event) => {
                selectLogoFile(event.currentTarget.files?.[0] ?? null);
                event.currentTarget.value = "";
              }}
              ref={logoInputRef}
              type="file"
            />
            {canEdit ? (
              <div
                className={`settings-page__logo-dropzone${logoDragging ? " settings-page__logo-dropzone--drag" : ""}${logoBusy ? " settings-page__logo-dropzone--busy" : ""}`}
                data-dragging={logoDragging || undefined}
                onDragEnter={onLogoDragEnter}
                onDragLeave={onLogoDragLeave}
                onDragOver={onLogoDragOver}
                onDrop={onLogoDrop}
              >
                <button
                  className="settings-page__logo-dropzone-button"
                  disabled={logoBusy}
                  id={logoDropzoneId}
                  onClick={() => logoInputRef.current?.click()}
                  type="button"
                >
                  <span className="settings-page__logo-dropzone-icon" aria-hidden="true">
                    {logoBusy
                      ? <LoaderCircle className="platform-spin" size={18} strokeWidth={1.8} />
                      : <Upload size={18} strokeWidth={1.8} />}
                  </span>
                  <span className="settings-page__logo-dropzone-title">
                    {logoBusy ? "Uploading logo…" : logoDragging ? "Drop to use this logo" : "Click to upload or drop an image"}
                  </span>
                  <span className="settings-page__logo-dropzone-description">
                    PNG, JPEG, or WebP · up to 1 MiB
                  </span>
                  <span className="settings-page__logo-dropzone-browse">
                    {logoBusy ? "Please wait" : "Browse files"}
                  </span>
                </button>
                {logoFile ? (
                  <div className="settings-page__logo-selected" aria-label="Selected logo file">
                    <ImagePlus aria-hidden="true" size={16} strokeWidth={1.7} />
                    <span className="settings-page__logo-selected-name">{logoFile.name}</span>
                    <span className="settings-page__logo-selected-size">{formatBytes(logoFile.size)}</span>
                    <button
                      aria-label={`Clear ${logoFile.name}`}
                      className="settings-page__logo-selected-clear"
                      disabled={logoBusy}
                      onClick={() => selectLogoFile(null)}
                      type="button"
                    >
                      Clear
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="settings-page__logo-readonly-hint">
                {settings.logoUrl ? "A logo is set for this organization." : "No logo uploaded yet."}
              </p>
            )}
            {logoError ? <p className="platform-field__error" role="alert">{logoError}</p> : null}
            {canEdit ? (
              <div className="settings-page__logo-actions">
                <button
                  className="platform-button platform-button--secondary"
                  disabled={!logoFile || logoBusy}
                  onClick={() => void uploadLogo()}
                  type="button"
                >
                  {logoBusy ? (
                    <>
                      <LoaderCircle aria-hidden="true" className="platform-spin" size={16} strokeWidth={1.8} />
                      Updating logo
                    </>
                  ) : (
                    "Upload logo"
                  )}
                </button>
                {settings.logoUrl ? (
                  <button
                    aria-label="Remove logo"
                    className="platform-icon-button"
                    disabled={logoBusy}
                    onClick={() => void removeLogo()}
                    title="Remove logo"
                    type="button"
                  >
                    <Trash2 aria-hidden="true" size={16} />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <form className="settings-page__form" onSubmit={save}>
        <section aria-labelledby="settings-organization-title" className="settings-page__section">
          <div className="dashboard-section-heading"><div><p className="dashboard-kicker">Organization</p><h2 id="settings-organization-title">Organization preferences</h2></div></div>
          <div className="settings-page__grid settings-page__grid--three">
            <SettingsField error={nameError} id="organization-name" label="Organization name"><input aria-describedby={nameError ? "organization-name-error" : undefined} aria-invalid={Boolean(nameError)} disabled={!canEdit} id="organization-name" maxLength={240} onChange={(event) => updateDraft("displayName", event.target.value)} value={draft.displayName} /></SettingsField>
            <SettingsField error={timezoneError} id="organization-timezone" label="Timezone"><select aria-describedby={timezoneError ? "organization-timezone-error" : undefined} aria-invalid={Boolean(timezoneError)} disabled={!canEdit} id="organization-timezone" onChange={(event) => updateDraft("timezone", event.target.value)} value={draft.timezone}>{timezoneOptions.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}</select></SettingsField>
            <SettingsField error={localeError} id="organization-locale" label="Locale"><select aria-describedby={localeError ? "organization-locale-error" : undefined} aria-invalid={Boolean(localeError)} disabled={!canEdit} id="organization-locale" onChange={(event) => updateDraft("locale", event.target.value)} value={draft.locale}>{localeOptions.map((locale) => <option key={locale} value={locale}>{locale}</option>)}</select></SettingsField>
          </div>
        </section>

        <section aria-labelledby="settings-session-title" className="settings-page__section">
          <div className="dashboard-section-heading"><div><p className="dashboard-kicker">Product access</p><h2 id="settings-session-title">Session policy</h2></div></div>
          <div className="settings-page__grid settings-page__grid--two">
            <SettingsField error={idleError} id="session-idle" label="Idle timeout (minutes)"><input aria-describedby={idleError ? "session-idle-error" : undefined} aria-invalid={Boolean(idleError)} disabled={!canEdit} id="session-idle" max={PRODUCT_SESSION_POLICY.idleMinutes.maximum} min={PRODUCT_SESSION_POLICY.idleMinutes.minimum} onChange={(event) => updateDraft("productSessionIdleMinutes", Number(event.target.value))} step={1} type="number" value={draft.productSessionIdleMinutes} /></SettingsField>
            <SettingsField error={maximumError} id="session-maximum" label="Maximum session length (hours)"><input aria-describedby={maximumError ? "session-maximum-error" : undefined} aria-invalid={Boolean(maximumError)} disabled={!canEdit} id="session-maximum" max={PRODUCT_SESSION_POLICY.maximumHours.maximum} min={PRODUCT_SESSION_POLICY.maximumHours.minimum} onChange={(event) => updateDraft("productSessionMaximumHours", Number(event.target.value))} step={1} type="number" value={draft.productSessionMaximumHours} /></SettingsField>
          </div>
        </section>

        <section aria-labelledby="settings-visibility-title" className="settings-page__section">
          <div className="dashboard-section-heading"><div><p className="dashboard-kicker">Visibility</p><h2 id="settings-visibility-title">Analytics and cost access</h2></div></div>
          <div className="settings-page__grid settings-page__grid--two">
            <VisibilityControl disabled={!canEdit} label="Analytics visibility" onChange={(value) => updateDraft("analyticsVisibility", value)} value={draft.analyticsVisibility} />
            <VisibilityControl disabled={!canEdit} label="Cost visibility" onChange={(value) => updateDraft("costVisibility", value)} value={draft.costVisibility} />
          </div>
        </section>

        {canEdit ? <div className="settings-page__form-actions"><button className="platform-button" disabled={saving || stale} type="submit">{saving ? "Saving settings" : "Save settings"}</button></div> : null}
      </form>

      <section aria-labelledby="settings-help-title" className="settings-page__help">
        <div className="dashboard-section-heading"><div><p className="dashboard-kicker">Help</p><h2 id="settings-help-title">Support and feedback</h2></div></div>
        <div className="settings-page__help-actions">
          <button aria-label="Contact support, Coming soon" className="platform-button platform-button--secondary" disabled type="button">Contact support <span>Coming soon</span></button>
          <button aria-label="Send feedback, Coming soon" className="platform-button platform-button--secondary" disabled type="button">Send feedback <span>Coming soon</span></button>
        </div>
      </section>
      {resource.status === "loading" ? <p className="dashboard-page__refreshing" role="status"><RotateCw aria-hidden="true" className="platform-spin" size={15} /> Refreshing settings.</p> : null}
    </section>
  );
}
